<template>
  <div class="analysis-wrapper">
    <el-row :gutter="20">
      <el-col :span="6" v-for="(chart, index) in charts" :key="index">
        <el-card class="chart-card" shadow="hover">
          <div class="chart-title">{{ chart.title }}</div>
          <div class="chart-container" :ref="'chart' + index"></div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'ManpowerAnalysis',
  props: {
    tableData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      charts: [
        {
          title: '巡检总特殊工单统计',
          data: [],
        },
        {
          title: '巡检不合格数',
          data: [],
        },
        {
          title: '巡检超时数',
          data: [],
        },
        {
          title: '巡检扣分工单数',
          data: [],
        },
      ],
      chartInstances: [],
    };
  },
  watch: {
    tableData: {
      handler(newData) {
        this.processChartData(newData);
      },
      immediate: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.chartInstances.forEach((chart) => {
      chart.dispose();
    });
  },
  methods: {
    processChartData(data) {
      // 处理园区统计数据
      const campusStats = {};
      data.forEach((item) => {
        const campusName = item.CampusName;
        if (!campusStats[campusName]) {
          campusStats[campusName] = {
            nopass: 0,
            timeout: 0,
            notfull: 0,
          };
        }

        if (item.FmcStatus === '不通过') {
          campusStats[campusName].nopass++;
        }
        if (item.TimeoutDuration !== 0) {
          campusStats[campusName].timeout++;
        }
        if (item.FmcStatus === '' || item.FmcStatus === null) {
          return;
        }
        if (item.Score !== 100) {
          campusStats[campusName].notfull++;
        }
      });

      // 计算总数据
      let totalNopass = 0;
      let totalTimeout = 0;
      let totalNotfull = 0;
      Object.values(campusStats).forEach((stat) => {
        totalNopass += stat.nopass;
        totalTimeout += stat.timeout;
        totalNotfull += stat.notfull;
      });
      const total = totalNopass + totalTimeout + totalNotfull;

      // 更新图表数据
      this.charts[0].data = [
        { name: '总数', value: total },
        { name: '不合格', value: totalNopass },
        { name: '超时', value: totalTimeout },
        { name: '未满分', value: totalNotfull },
      ];

      // 更新各园区数据
      const campusData = Object.entries(campusStats).map(([name, stats]) => ({
        name,
        nopass: stats.nopass,
        timeout: stats.timeout,
        notfull: stats.notfull,
      }));

      this.charts[1].data = campusData.map((item) => ({
        name: item.name,
        value: item.nopass,
      }));

      this.charts[2].data = campusData.map((item) => ({
        name: item.name,
        value: item.timeout,
      }));

      this.charts[3].data = campusData.map((item) => ({
        name: item.name,
        value: item.notfull,
      }));

      // 重新初始化图表
      this.initCharts();
    },
    initCharts() {
      console.log(this.charts);
      this.charts.forEach((chart, index) => {
        const chartDom = this.$refs['chart' + index][0];
        const myChart = echarts.init(chartDom);
        this.chartInstances[index] = myChart;

        // 检查数据是否为空或所有value为0
        const hasData = chart.data && chart.data.length > 0;
        const allZero = chart.data.every((item) => item.value === 0);
        const option = {
          title: allZero
            ? {
                text: 'PASS',
                textStyle: {
                  fontSize: 30,
                  fontWeight: 'normal',
                  color: '#A6EC9A',
                  rotate: -45,
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                },
                left: 'center',
                top: 'center',
              }
            : {
                text: '',
                textStyle: {
                  fontSize: 14,
                  fontWeight: 'normal',
                  color: '#999',
                },
                left: 'center',
                top: 'center',
              },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
            textStyle: {
              fontSize: 12,
            },
            formatter: '{b}: {c}',
          },
          grid: {
            top: '15%',
            left: '3%',
            right: '4%',
            bottom: '12%',
            containLabel: true,
          },
          xAxis: [
            {
              type: 'category',
              data: hasData ? chart.data.map((item) => item.name) : [],
              axisTick: {
                show: false,
              },
              axisLine: {
                lineStyle: {
                  color: '#E0E6F1',
                },
              },
              axisLabel: {
                interval: 0,
                rotate: 45,
                textStyle: {
                  fontSize: 12,
                  color: '#666',
                },
                overflow: 'truncate',
              },
            },
          ],
          yAxis: [
            {
              type: 'value',
              splitLine: {
                lineStyle: {
                  type: 'dashed',
                  color: '#E0E6F1',
                },
              },
              axisLine: {
                show: false,
              },
              axisTick: {
                show: false,
              },
              axisLabel: {
                textStyle: {
                  fontSize: 12,
                  color: '#666',
                },
                formatter: function (value) {
                  if (allZero) {
                    return '';
                  }
                  if (value % 1 !== 0) {
                    return '';
                  }
                  return value;
                },
              },
            },
          ],
          series: [
            {
              name: chart.title,
              type: 'bar',
              barWidth: '20%', // 设置柱状图宽度为容器宽度的20%
              data: hasData
                ? chart.data.map((item) => ({
                    value: item.value,
                    itemStyle: {
                      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: '#4B79E5' },
                        { offset: 1, color: '#38BDF8' },
                      ]),
                    },
                  }))
                : [],
              emphasis: {
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: '#6366F1' },
                    { offset: 1, color: '#0EA5E9' },
                  ]),
                },
              },
              label: {
                show: true,
                position: 'top',
                textStyle: {
                  fontSize: 12,
                  color: '#666',
                },
              },
            },
          ],
        };

        myChart.setOption(option);
      });
    },
    handleResize() {
      this.chartInstances.forEach((chart) => {
        chart.resize();
      });
    },
  },
};
</script>

<style scoped>
.chart-card {
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid #e4e9f2;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  background: #fff;
}
.chart-card:hover {
  border-color: #c0cde0;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.chart-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 24px;
  text-align: center;
  position: relative;
  padding-bottom: 12px;
}

.chart-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #4b79e5, #38bdf8);
  border-radius: 2px;
}

.chart-container {
  height: 320px;
  width: 100%;
  padding: 12px;
  padding-right: 30px;
}
</style>